import { getApiClient } from 'api/apiClient'
import { API_ROUTES } from 'constants/apiRoutesConstants'

import { showNotification } from '@/components/Notification'
import { transformBackendResponseKey } from '@/config'
import { DEFAULT_LOCALE, ROUTES } from '@/constants'
import { GENERATOR_SEARCH_PARAM, TQrData } from '@/generator/shared'
import { TRouter } from '@/i18n-routing'
import { getStore } from '@/store'
import { env } from '@/utils'

import { TABLE_ACTION_TYPE } from '../TableActions/Actions/actionOptions'
import { qrListAtom } from '../tableState'

const getErrorMessage = (error: unknown, t: (key: string) => string) => {
	if (error && typeof error === 'object' && 'response' in error) {
		const apiError = error as { response?: { data?: { message?: string } } }
		if (apiError.response?.data?.message) {
			return t(transformBackendResponseKey(apiError.response.data.message))
		}
	}
	return t('public__generic__unexpected_error')
}

export type TranslationKey = {
	title: string
	subtitle: string
	description: string
	confirmButtonLabel?: string
	onConfirm: (
		qrItem: TQrData,
		router: TRouter,
		t?: (key: string) => string,
		locale?: string,
	) => Promise<void>
}

export type BulkTranslationKey = {
	title: string
	subtitle: string
	description: string
	confirmButtonLabel?: string
	onConfirm: (
		selectedQrs: TQrData[],
		router: TRouter,
		t?: (key: string) => string,
	) => Promise<void>
}

export const actionTypeToConfirmationDialogConfig: Partial<
	Record<Exclude<TABLE_ACTION_TYPE, null>, TranslationKey>
> = {
	[TABLE_ACTION_TYPE.ChangeQrType]: {
		title: 'dashboard__change_qr_type__modal__title',
		subtitle: 'dashboard__change_qr_type__modal__subtitle',
		description: 'dashboard__change_qr_type__modal__description',
		onConfirm: async (qrItem, router, t, locale) => {
			if (!qrItem?.id) return

			const baseUrl = env.HOST
			const pathname =
				locale && locale !== DEFAULT_LOCALE
					? `${baseUrl}/${locale}${ROUTES.generator.typeStep}`
					: `${baseUrl}/${ROUTES.generator.typeStep}`

			await router.push({
				pathname,
				query: {
					[GENERATOR_SEARCH_PARAM.Target]: qrItem.id,
					[GENERATOR_SEARCH_PARAM.Edit]: 'type',
				},
			})
		},
	},
	[TABLE_ACTION_TYPE.Delete]: {
		title: 'dashboard__delete__qr_code__modal_title',
		subtitle: 'dashboard__delete__qr_code__modal_subtitle',
		description: 'dashboard__delete__qr_code__modal_description',
		confirmButtonLabel: 'dashboard__account__settings__delete_account__button',
		onConfirm: async (qrItem, router, t) => {
			if (!qrItem?.id) return

			try {
				await getApiClient().delete(API_ROUTES.qrCode.base, {
					data: {
						ids: [qrItem.id],
					},
				})

				const qrList = getStore().get(qrListAtom)
				getStore().set(
					qrListAtom,
					qrList.filter((qr) => qr.id !== qrItem.id),
				)
				if (t) {
					showNotification(t('dashboard__delete__qr_code__modal_success'), {
						type: 'success',
					})
				}
			} catch (error) {
				console.error(error)
				if (t) {
					showNotification(getErrorMessage(error, t), {
						type: 'error',
					})
				}
			}
		},
	},
	[TABLE_ACTION_TYPE.ResetScans]: {
		title: 'dashboard__reset_scans__qr_code__modal_title',
		subtitle: 'dashboard__reset_scans__qr_code__modal_subtitle',
		description: 'dashboard__reset_scans__qr_code__modal_description',
		onConfirm: async (qrItem, router, t) => {
			if (!qrItem?.id) return

			try {
				await getApiClient().post(API_ROUTES.qrCode.resetScans, {
					ids: [qrItem.id],
				})

				const qrList = getStore().get(qrListAtom)
				getStore().set(
					qrListAtom,
					qrList.map((qr) => {
						if (qr.id === qrItem.id) {
							return {
								...qr,
								scansAmount: 0,
							}
						}

						return qr
					}),
				)
			} catch (error) {
				console.error(error)
				if (t) {
					showNotification(getErrorMessage(error, t), {
						type: 'error',
					})
				}
			}
		},
	},
	[TABLE_ACTION_TYPE.Pause]: {
		title: 'dashboard__pause__qr_code__modal_title',
		subtitle: 'dashboard__pause__qr_code__modal_subtitle',
		description: 'dashboard__pause__qr_code__modal_description',
		onConfirm: async (qrItem, router, t) => {
			if (!qrItem?.id) return

			try {
				await getApiClient().post(API_ROUTES.qrCode.deactivate, {
					ids: [qrItem.id],
				})

				const qrList = getStore().get(qrListAtom)
				getStore().set(
					qrListAtom,
					qrList.map((qr) => {
						if (qr.id === qrItem.id) {
							return {
								...qr,
								disabled: true,
							}
						}

						return qr
					}),
				)
			} catch (error) {
				console.error(error)
				if (t) {
					showNotification(getErrorMessage(error, t), {
						type: 'error',
					})
				}
			}
		},
	},
	[TABLE_ACTION_TYPE.Activate]: {
		title: 'dashboard__activate__qr_code__modal_title',
		subtitle: 'dashboard__activate__qr_code__modal_subtitle',
		description: 'dashboard__activate__qr_code__modal_description',
		onConfirm: async (qrItem, router, t) => {
			if (!qrItem?.id) return

			try {
				await getApiClient().post(API_ROUTES.qrCode.activate, {
					ids: [qrItem.id],
				})

				const qrList = getStore().get(qrListAtom)
				getStore().set(
					qrListAtom,
					qrList.map((qr) => {
						if (qr.id === qrItem.id) {
							return {
								...qr,
								disabled: false,
							}
						}

						return qr
					}),
				)
			} catch (error) {
				console.error(error)
				if (t) {
					showNotification(getErrorMessage(error, t), {
						type: 'error',
					})
				}
			}
		},
	},
}

export const bulkActionToConfirmationDialogConfig: Partial<
	Record<TABLE_ACTION_TYPE, BulkTranslationKey>
> = {
	[TABLE_ACTION_TYPE.BulkPause]: {
		title: 'dashboard__pause__qr_code__modal_title',
		subtitle: 'dashboard__pause__qr_code__modal_subtitle',
		description: 'dashboard__pause__qr_code__modal_description',
		onConfirm: async (selectedQrs, router, t) => {
			const ids = selectedQrs.map((qr) => qr.id)

			try {
				await getApiClient().post(API_ROUTES.qrCode.deactivate, { ids })

				const qrList = getStore().get(qrListAtom)
				getStore().set(
					qrListAtom,
					qrList.map((qr) =>
						ids.includes(qr.id) ? { ...qr, disabled: true } : qr,
					),
				)
			} catch (error) {
				console.error('Failed to pause QR codes:', error)
				if (t) {
					showNotification(getErrorMessage(error, t), {
						type: 'error',
					})
				}
			}
		},
	},
	[TABLE_ACTION_TYPE.BulkActivate]: {
		title: 'dashboard__activate__qr_code__modal_title',
		subtitle: 'dashboard__activate__qr_code__modal_subtitle',
		description: 'dashboard__activate__qr_code__modal_description',
		onConfirm: async (selectedQrs, router, t) => {
			const ids = selectedQrs.map((qr) => qr.id)

			try {
				await getApiClient().post(API_ROUTES.qrCode.activate, { ids })

				const qrList = getStore().get(qrListAtom)
				getStore().set(
					qrListAtom,
					qrList.map((qr) =>
						ids.includes(qr.id) ? { ...qr, disabled: false } : qr,
					),
				)
			} catch (error) {
				console.error('Failed to activate QR codes:', error)
				if (t) {
					showNotification(getErrorMessage(error, t), {
						type: 'error',
					})
				}
			}
		},
	},
	[TABLE_ACTION_TYPE.BulkResetScans]: {
		title: 'dashboard__reset_scans__qr_code__modal_title',
		subtitle: 'dashboard__bulk_reset_scans__qr_code__modal_subtitle',
		description: 'dashboard__bulk_reset_scans__qr_code__modal_description',
		onConfirm: async (selectedQrs, router, t) => {
			const ids = selectedQrs.map((qr) => qr.id)

			try {
				await getApiClient().post(API_ROUTES.qrCode.resetScans, { ids })

				const qrList = getStore().get(qrListAtom)
				getStore().set(
					qrListAtom,
					qrList.map((qr) =>
						ids.includes(qr.id) ? { ...qr, scansAmount: 0 } : qr,
					),
				)
			} catch (error) {
				console.error('Failed to reset scans:', error)
				if (t) {
					showNotification(getErrorMessage(error, t), {
						type: 'error',
					})
				}
			}
		},
	},
	[TABLE_ACTION_TYPE.BulkDelete]: {
		title: 'dashboard__delete__qr_code__modal_title',
		subtitle: 'dashboard__bulk__delete__qr_code__modal_subtitle',
		description: 'dashboard__bulk__delete__qr_code__modal_description',
		confirmButtonLabel: 'dashboard__account__settings__delete_account__button',
		onConfirm: async (selectedQrs, router, t) => {
			const ids = selectedQrs.map((qr) => qr.id)

			try {
				await getApiClient().delete(API_ROUTES.qrCode.base, { data: { ids } })

				const qrList = getStore().get(qrListAtom)
				getStore().set(
					qrListAtom,
					qrList.filter((qr) => !ids.includes(qr.id)),
				)
				if (t) {
					showNotification(
						t('dashboard__bulk__delete__qr_code__modal_success'),
						{
							type: 'success',
						},
					)
				}
			} catch (error) {
				console.error('Failed to delete QR codes:', error)
				if (t) {
					showNotification(getErrorMessage(error, t), {
						type: 'error',
					})
				}
			}
		},
	},
}
